import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { locationsApi } from '@/services/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { MapPinIcon, PlusIcon, XMarkIcon, PencilIcon, TrashIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Location {
  id: number;
  name: string;
  location_type: 'country' | 'state' | 'city' | 'district' | 'area';
  parent_id: number | null;
  country_code: string | null;
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
  sort_order: number;
}

const LocationManagementPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [selectedType, setSelectedType] = useState<'country' | 'state' | 'city' | 'district' | 'area'>('country');
  const [formData, setFormData] = useState({
    name: '',
    location_type: 'country' as 'country' | 'state' | 'city' | 'district' | 'area',
    parent_id: '',
    country_code: '',
    latitude: '',
    longitude: '',
    is_active: true,
    sort_order: 0,
  });
  
  const [parentOptions, setParentOptions] = useState<Location[]>([]);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    // Update parent options when location type changes
    if (formData.location_type === 'country') {
      setParentOptions([]);
      setFormData(prev => ({ ...prev, parent_id: '' }));
    } else {
      // Get parent type based on current type
      const parentTypeMap: Record<string, string> = {
        'state': 'country',
        'city': 'state',
        'district': 'city',
        'area': 'district'
      };
      const parentType = parentTypeMap[formData.location_type];
      if (parentType) {
        fetchParentOptions(parentType);
      }
    }
  }, [formData.location_type]);

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      // Fetch all locations by type
      const results = await Promise.allSettled([
        locationsApi.getAll('country'),
        locationsApi.getAll('state'),
        locationsApi.getAll('city'),
        locationsApi.getAll('district'),
        locationsApi.getAll('area'),
      ]);
      
      const allLocations: Location[] = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.data) {
          allLocations.push(...(result.value.data || []));
        }
      });
      
      setLocations(allLocations);
    } catch (error: any) {
      toast.error('Failed to fetch locations');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParentOptions = async (parentType: string) => {
    try {
      const response = await locationsApi.getAll(parentType);
      setParentOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching parent options:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'country' | 'state' | 'city' | 'district' | 'area';
    setSelectedType(newType);
    setFormData(prev => ({
      ...prev,
      location_type: newType,
      parent_id: '', // Reset parent when type changes
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const locationData: any = {
        name: formData.name,
        location_type: formData.location_type,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        country_code: formData.country_code || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        is_active: formData.is_active,
        sort_order: formData.sort_order || 0,
      };

      if (editingLocation) {
        await locationsApi.update(editingLocation.id, locationData);
        toast.success('Location updated successfully!');
      } else {
        await locationsApi.create(locationData);
        toast.success('Location created successfully!');
      }
      
      setShowCreateForm(false);
      setEditingLocation(null);
      resetForm();
      fetchLocations();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || `Failed to ${editingLocation ? 'update' : 'create'} location`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location_type: 'country',
      parent_id: '',
      country_code: '',
      latitude: '',
      longitude: '',
      is_active: true,
      sort_order: 0,
    });
    setSelectedType('country');
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      location_type: location.location_type,
      parent_id: location.parent_id?.toString() || '',
      country_code: location.country_code || '',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || '',
      is_active: location.is_active ?? true,
      sort_order: location.sort_order || 0,
    });
    setSelectedType(location.location_type);
    setShowCreateForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      return;
    }
    
    try {
      await locationsApi.delete(id);
      toast.success('Location deleted successfully');
      fetchLocations();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
      console.error(error);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingLocation(null);
    resetForm();
  };

  const getLocationsByType = (type: string) => {
    return locations.filter(loc => loc.location_type === type);
  };

  const getLocationName = (id: number | null) => {
    if (!id) return 'N/A';
    const location = locations.find(loc => loc.id === id);
    return location ? location.name : 'Unknown';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Location Management</h1>
            <p className="mt-2 text-gray-600">Manage countries, states, districts, and areas</p>
          </div>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {showCreateForm ? 'Cancel' : 'Create Location'}
          </Button>
        </div>

        {/* Create/Edit Location Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingLocation ? 'Edit Location' : 'Create New Location'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location Type *
                </label>
                <select
                  name="location_type"
                  value={formData.location_type}
                  onChange={(e) => {
                    handleTypeChange(e);
                    handleInputChange(e);
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="country">Country</option>
                  <option value="state">State/Province</option>
                  <option value="city">City</option>
                  <option value="district">District</option>
                  <option value="area">Area</option>
                </select>
              </div>

              <Input
                label="Location Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder={`e.g., ${formData.location_type === 'country' ? 'Bangladesh' : formData.location_type === 'state' ? 'Dhaka' : 'Gulshan'}`}
              />

              {formData.location_type !== 'country' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parent {formData.location_type === 'state' ? 'Country' : formData.location_type === 'city' ? 'State' : formData.location_type === 'district' ? 'City' : 'District'} *
                  </label>
                  <select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select parent location</option>
                    {parentOptions.map((parent) => (
                      <option key={parent.id} value={parent.id}>
                        {parent.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.location_type === 'country' && (
                <Input
                  label="Country Code (ISO 3-letter, e.g., BGD)"
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleInputChange}
                  placeholder="BGD"
                  maxLength={3}
                />
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Latitude (Optional)"
                  name="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  placeholder="e.g., 23.8103"
                />
                <Input
                  label="Longitude (Optional)"
                  name="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  placeholder="e.g., 90.4125"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Sort Order"
                  name="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  placeholder="0"
                />
                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit">{editingLocation ? 'Update Location' : 'Create Location'}</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Locations List by Type */}
        <div className="space-y-6">
          {/* Countries */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <GlobeAltIcon className="h-6 w-6 text-primary-600 mr-2" />
                Countries
              </h2>
              <LocationList
                locations={getLocationsByType('country')}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getLocationName={getLocationName}
              />
            </div>
          </div>

          {/* States */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPinIcon className="h-6 w-6 text-blue-600 mr-2" />
                States/Provinces
              </h2>
              <LocationList
                locations={getLocationsByType('state')}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getLocationName={getLocationName}
              />
            </div>
          </div>

          {/* Cities */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPinIcon className="h-6 w-6 text-green-600 mr-2" />
                Cities
              </h2>
              <LocationList
                locations={getLocationsByType('city')}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getLocationName={getLocationName}
              />
            </div>
          </div>

          {/* Districts */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPinIcon className="h-6 w-6 text-yellow-600 mr-2" />
                Districts
              </h2>
              <LocationList
                locations={getLocationsByType('district')}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getLocationName={getLocationName}
              />
            </div>
          </div>

          {/* Areas */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPinIcon className="h-6 w-6 text-purple-600 mr-2" />
                Areas
              </h2>
              <LocationList
                locations={getLocationsByType('area')}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getLocationName={getLocationName}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Location List Component
const LocationList: React.FC<{
  locations: Location[];
  onEdit: (location: Location) => void;
  onDelete: (id: number) => void;
  getLocationName: (id: number | null) => string;
}> = ({ locations, onEdit, onDelete, getLocationName }) => {
  if (locations.length === 0) {
    return <p className="text-gray-400 italic">No locations found</p>;
  }

  return (
    <div className="space-y-3">
      {locations.map((location) => (
        <div
          key={location.id}
          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <div className="flex-1">
            <div className="flex items-center">
              <h3 className="text-lg font-medium text-gray-900">{location.name}</h3>
              {location.parent_id && (
                <span className="ml-3 text-sm text-gray-500">
                  (Parent: {getLocationName(location.parent_id)})
                </span>
              )}
              {location.country_code && (
                <span className="ml-3 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                  {location.country_code}
                </span>
              )}
              <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                location.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {location.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {location.latitude && location.longitude && (
              <p className="mt-1 text-sm text-gray-500">
                Coordinates: {location.latitude}, {location.longitude}
              </p>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => onEdit(location)}
              className="text-primary-600 hover:text-primary-900"
              title="Edit"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => onDelete(location.id)}
              className="text-red-600 hover:text-red-900"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LocationManagementPage;

